import { AlertTriangle, ArrowLeftRight, Percent, RefreshCw, ShieldCheck, UserRoundCheck } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaProgress } from "@/shared/components/feedback/ColocaProgress";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaStepper } from "@/shared/components/navigation/ColocaStepper";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import useTranslations from "@/shared/lang/useTranslations";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { colocaToast } from "@/shared/utils/toast";

import { RATE_DRIFT_TOLERANCE_PCT } from "../../constants/payoutBatches.constants";
import { useArmDelay } from "../../hooks/useArmDelay";
import { useBatchErrorMessage } from "../../hooks/useBatchErrorMessage";
import { useExecuteBatch } from "../../hooks/useExecuteBatch";
import type { PayoutBatchDetail, SelfApprovalReason } from "../../types/payoutBatches.types";
import { isProviderBalanceAtRisk } from "../../utils/batchSummary";
import { ConsequenceList, ConsequenceRow } from "./ConsequenceList";

export interface ExecuteBatchDialogProps {
  batch: PayoutBatchDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isInternal: boolean;
  applyBatch: (next: PayoutBatchDetail) => void;
  refresh: () => Promise<PayoutBatchDetail | null>;
  /** Set when the viewer approves a batch they prepared: it goes out without a second person. */
  selfApproval: SelfApprovalReason | null;
}

const SELF_APPROVAL_NOTICE_KEYS = {
  org_setting: "execute_self_approval_org_setting",
  sole_approver: "execute_self_approval_sole_approver",
} as const;

/**
 * Step 3: what is about to happen, stated once and confirmed twice — a checkbox after a reading delay, then
 * the TOTP. The `fingerprint` echoed to the server is the one of the summary on screen; a 409 means it no
 * longer describes the batch and the only exit is back to the review, never a retry. A maker approving their
 * own batch is told so, since nobody else will look at it.
 */
export function ExecuteBatchDialog({
  batch,
  open,
  onOpenChange,
  isInternal,
  applyBatch,
  refresh,
  selfApproval,
}: ExecuteBatchDialogProps) {
  const { t } = useTranslations("payoutBatches");
  const describeError = useBatchErrorMessage();
  const { stage, error, execute, reset, twoFactor } = useExecuteBatch(batch.id, applyBatch, refresh);
  const { armed, disarm } = useArmDelay(open);
  const [reviewed, setReviewed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const source = batch.source_currency;
  const delivery = batch.currency;
  const crossCurrency = source !== delivery;
  const tolerancePct = batch.summary.drift_tolerance_pct ?? RATE_DRIFT_TOLERANCE_PCT;
  const needsAcknowledgement =
    isInternal && isProviderBalanceAtRisk(batch.summary.provider_balance, batch.totals.receive_selected);
  const sending = stage === "sending";

  const close = () => {
    setReviewed(false);
    setAcknowledged(false);
    disarm();
    reset();
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && sending) return;
    if (!next) close();
    else onOpenChange(true);
  };

  const confirm = async () => {
    if (!batch.summary.fingerprint) return;
    const outcome = await execute({
      fingerprint: batch.summary.fingerprint,
      acknowledgeProviderBalance: needsAcknowledgement && acknowledged,
    });
    if (outcome === "done") {
      colocaToast.success(t("execute_success_toast"));
      close();
    }
  };

  const backToReview = async () => {
    await refresh();
    close();
  };

  const canConfirm =
    armed &&
    reviewed &&
    (!needsAcknowledgement || acknowledged) &&
    !sending &&
    batch.summary.fingerprint !== null;

  return (
    <>
      <ColocaDialog
        open={open}
        onOpenChange={handleOpenChange}
        size="lg"
        title={t("execute_title")}
        description={stage === "form" || sending ? t("execute_description") : undefined}
        showCloseButton={!sending}
        toolbar={
          <div className="border-b border-border px-6 py-3">
            <ColocaStepper
              steps={[
                { label: t("step_configure") },
                { label: t("step_review") },
                { label: t("step_approval") },
                { label: t("step_confirm") },
              ]}
              currentStep={2}
              aria-label={t("execute_title")}
            />
          </div>
        }
        footer={
          stage === "conflict" ? (
            <ColocaButton variant="primary" icon={RefreshCw} onClick={() => void backToReview()}>
              {t("execute_conflict_back")}
            </ColocaButton>
          ) : stage === "unconfirmed" ? (
            <ColocaButton variant="primary" onClick={() => void backToReview()}>
              {t("action_close")}
            </ColocaButton>
          ) : (
            <>
              <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={sending} />}>
                {t("action_cancel")}
              </ColocaDialogClose>
              <ColocaButton
                variant="primary"
                icon={ShieldCheck}
                loading={sending}
                disabled={!canConfirm}
                onClick={() => void confirm()}
              >
                {sending ? t("execute_sending") : armed ? t("execute_confirm") : t("execute_arming")}
              </ColocaButton>
            </>
          )
        }
      >
        {stage === "conflict" && error ? (
          error.kind === "self_approval" ? (
            <ColocaAlert
              variant="destructive"
              icon={AlertTriangle}
              heading={t("execute_self_approval_revoked_title")}
              text={t("execute_self_approval_revoked_description")}
            />
          ) : (
            <ColocaAlert
              variant="destructive"
              icon={AlertTriangle}
              heading={t("execute_conflict_title")}
              text={error.code === "organization_busy" ? t("execute_busy_description") : describeError(error)}
            />
          )
        ) : stage === "unconfirmed" ? (
          <ColocaAlert
            variant="warning"
            icon={AlertTriangle}
            heading={t("execute_unconfirmed_title")}
            text={t("execute_unconfirmed_description")}
          />
        ) : (
          <div className="space-y-5">
            <ColocaProgress
              value={batch.counters.selected}
              max={Math.max(batch.counters.valid, 1)}
              size="sm"
              tone="primary"
              label={t("execute_payments", { count: batch.counters.selected })}
              aria-label={t("execute_payments", {
                count: batch.counters.selected,
              })}
            />

            <ConsequenceList>
              <ConsequenceRow
                label={t("execute_receive")}
                value={
                  <ColocaAmountDisplay
                    value={batch.totals.receive_selected}
                    currency={delivery}
                    fractionDigits={minorUnitsFor(delivery)}
                    size="sm"
                  />
                }
              />
              <ConsequenceRow
                label={t("execute_fees")}
                value={
                  <ColocaAmountDisplay
                    value={batch.totals.fees_selected}
                    currency={delivery}
                    fractionDigits={minorUnitsFor(delivery)}
                    size="sm"
                  />
                }
              />
              <ConsequenceRow
                label={t("execute_debit")}
                emphasis
                value={
                  <ColocaAmountDisplay
                    value={batch.totals.debit_indicative_selected}
                    currency={source}
                    fractionDigits={minorUnitsFor(source)}
                    size="lg"
                  />
                }
              />
            </ConsequenceList>

            <ColocaAlert
              variant="secondary"
              icon={crossCurrency ? ArrowLeftRight : Percent}
              text={t(crossCurrency ? "execute_fx_notice" : "execute_fee_notice", { pct: tolerancePct })}
            />

            {selfApproval && (
              <ColocaAlert
                variant="secondary"
                icon={UserRoundCheck}
                text={t(SELF_APPROVAL_NOTICE_KEYS[selfApproval])}
              />
            )}

            <ColocaAlert variant="warning" icon={AlertTriangle} text={t("execute_irreversible")} />

            {error && stage === "form" && (
              <ColocaAlert variant="destructive" icon={AlertTriangle} text={describeError(error)} />
            )}

            <div className="space-y-3">
              {needsAcknowledgement && (
                <ColocaCheckbox
                  checked={acknowledged}
                  onCheckedChange={setAcknowledged}
                  disabled={sending}
                  label={t("execute_ack_liquidity_label")}
                  description={t("execute_ack_liquidity_description")}
                />
              )}
              <ColocaCheckbox
                checked={reviewed}
                onCheckedChange={setReviewed}
                disabled={sending}
                label={t("execute_reviewed_label")}
              />
            </div>
          </div>
        )}
      </ColocaDialog>
      <TwoFactorActionModals controller={twoFactor} requiredDescription={t("execute_two_factor_required")} />
    </>
  );
}
